import { useContext } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { Button, Text } from "@shopify/polaris";
import styles from "../../../../components/TemplateSelectionScreen/TemplateSelectionScreen.module.css";
import cardStyles from "../VolumeFormPreviewWrapper/VolumeBundleTemplate.module.css";
import { VolumePreview } from "../VolumeBundle/VolumeBundle";
import { AppConfigContext } from "../../../../context/AppConfigContext";
import { DUMMY_PRODUCTS } from "./dummyVolumeProducts";
import { OfferInterface, ProductObj, VolumeConfigInterface } from "../../Types";
import { AvailableVolumeTemplates, DISCOUNT_TYPES } from "../../constants";
import useIsMobile from "../../../../hooks/useIsMobile";

const VolumeTemplateLivePreview = ({
    onSelectTemplate,
    layout,
}: {
    onSelectTemplate: (template: string, overrides?: Partial<VolumeConfigInterface>) => void;
    layout: "vertical" | "horizontal";
}) => {
    const { isMobile } = useIsMobile();
    const { control } = useFormContext();
    const selectedTemplate = useWatch({ control, name: "selected_template_volume" });
    const bundlesDesign = useWatch({ control, name: "bundles_design" });
    const products = useWatch({ control, name: "products" }) as ProductObj[] | undefined;
    const liveConfig = useWatch({ control }) as any as VolumeConfigInterface;

    const appConfigContext = useContext(AppConfigContext);

    const previewProducts: ProductObj[] = products?.length ? products : [DUMMY_PRODUCTS.standardWithVariant];

    const getOffers = (
        count: number,
        overridesByIndex: Record<number, Partial<OfferInterface>> = {},
    ): OfferInterface[] => {
        const source = liveConfig.volume_discounts?.length
            ? liveConfig.volume_discounts
            : Array.from({ length: count }, () => ({}) as OfferInterface);
        const offers = source.slice(0, count);
        return offers.map((offer, i) => {
            const overrides = overridesByIndex[i] ?? overridesByIndex[i - offers.length];
            return overrides ? { ...offer, ...overrides } : offer;
        });
    };

    const renderTemplateCard = (
        template: string,
        label: string,
        configOverrides: Partial<VolumeConfigInterface> = {},
        cardKey: string = template,
    ) => {
        const isSelected = selectedTemplate === template;
        return (
            <div
                key={cardKey}
                className={`${cardStyles.container} ${cardStyles.volumeCard} ${styles.volumeCardCompact} ${
                    isSelected ? cardStyles.volumeCardActive : ""
                }`}
            >
                <VolumePreview
                    bundleConfig={{
                        ...liveConfig,
                        bundles_design: bundlesDesign,
                        products: previewProducts,
                        selected_template_volume: template,
                        show_variant_selector: !template.startsWith("horizontal"),
                        general_settings: { show_powered_by_kefi: false } as any,
                        ...configOverrides,
                    }}
                    standAlone={false}
                    appConfigContext={appConfigContext}
                    pId={0}
                />
                <div className={styles.templateSelectionCardBottomRow} style={{ width: "100%" }}>
                    <div className={styles.templateSelectionCardLabelRow}>
                        <Text as="span" variant="headingMd" fontWeight="semibold">
                            {label}
                        </Text>
                    </div>
                    <Button
                        size="large"
                        variant="secondary"
                        onClick={() => onSelectTemplate(template, configOverrides)}
                    >
                        {isSelected ? "Selected" : "Select"}
                    </Button>
                </div>
            </div>
        );
    };

    return (
        <div
            className={`${styles.templateSelectionGrid} ${styles.custom}`}
            style={{
                gridTemplateColumns: isMobile
                    ? "repeat(1, 1fr)"
                    : layout === "horizontal"
                      ? "repeat(2, 1fr)"
                      : "repeat(3, 1fr)",
            }}
        >
            {layout === "vertical" && (
                <>
                    {renderTemplateCard(AvailableVolumeTemplates.VERTICAL_CLASSIC, "Free Gift with Purchase", {
                        default_offer: 1,
                        bundles_design: { ...liveConfig.bundles_design, default_cta: false },
                        add_to_cart_cta_text: "Add to Cart",
                        show_option_labels_once: true,
                        hide_item_numbers: true,
                        volume_discounts: getOffers(2, {
                            1: {
                                allow_free_gifts: true,
                                free_gifts: [DUMMY_PRODUCTS.standardWithVariant],
                                discount_label_type: "flag",
                            },
                        }),
                    })}
                    {renderTemplateCard(AvailableVolumeTemplates.VERTICAL_CLASSIC, "Product Bundle Offer", {
                        default_offer: 1,
                        hide_item_numbers: true,
                        variant_label_position: "beside",
                        products: [DUMMY_PRODUCTS.subscriptionWithVariant],
                        add_to_cart_cta_icon_type: "arrow",
                        add_to_cart_cta_text: "{icon} Add to Cart",
                        volume_discounts: getOffers(2, {
                            1: {
                                offer_type: "product_bundle",
                                offer_title: "Complete the bundle",
                                bundle_products: [DUMMY_PRODUCTS.subscriptionWithVariant],
                                discount_label_type: "flag",
                            },
                        }),
                        bundles_design: { ...liveConfig.bundles_design, default_cta: false },
                    })}
                    {renderTemplateCard(AvailableVolumeTemplates.VERTICAL_MODERN, "Upsell Products", {
                        default_offer: 1,
                        volume_discounts: getOffers(2, {
                            1: {
                                tag: "",
                                discount_label: "Most Popular",
                                discount_label_type: "ribbon",
                                allow_upsell_products: true,
                                upsell_products: [DUMMY_PRODUCTS.standardWithVariant],
                            },
                        }),
                        bundles_design: {
                            ...liveConfig.bundles_design,
                            default_cta: false,
                            discount_label_font_size: 30,
                        },
                    })}
                    {renderTemplateCard(
                        AvailableVolumeTemplates.VERTICAL_CLASSIC,
                        "Subscription Ready",
                        {
                            enable_subscription_integration: true,
                            default_offer: 1,
                            products: [DUMMY_PRODUCTS.subscriptionWithVariant],
                            volume_discounts: getOffers(3, { 1: { discount_label_type: "flag" } }),
                            bundles_design: { ...liveConfig.bundles_design, default_cta: false },
                        },
                        "vertical_basic_subscription",
                    )}
                    {renderTemplateCard(
                        AvailableVolumeTemplates.VERTICAL_ELEGANT,
                        "Savings Summary & Countdown",
                        {
                            show_countdown_timer: true,
                            add_to_cart_cta_icon_type: "arrow",
                            add_to_cart_cta_text: "{icon} Add to Cart",
                            products: [DUMMY_PRODUCTS.standardWithVariant],
                            savings_summary: {
                                ...liveConfig.savings_summary,
                                status: true,
                                title: "{icon} You're saving {saved_amount} on this order {icon}",
                                icon_type: "star",
                            },
                            volume_discounts: getOffers(3, {
                                0: {
                                    discount_type: DISCOUNT_TYPES.PERCENTAGE,
                                    discount_value: 10,
                                },
                                1: { discount_label_type: "flag" },
                            }),
                            bundles_design: {
                                ...liveConfig.bundles_design,
                                default_cta: false,
                                special_text_color: "#979797",
                            },
                        },
                        "vertical_elegant_savings_summary",
                    )}
                    {renderTemplateCard(
                        AvailableVolumeTemplates.VERTICAL_MODERN,
                        "Countdown Timer & Unit Price",
                        {
                            show_countdown_timer: true,
                            show_unit_price: true,
                            volume_discounts: getOffers(3, {
                                0: { tag: "", discount_label: "Standard Price", discount_label_type: "tag" },
                                1: { discount_label: "" },
                            }),
                            bundles_design: {
                                ...liveConfig.bundles_design,
                                default_cta: false,
                                discount_label_font_size: 30,
                            },
                        },
                        "vertical_modern_countdown_timer",
                    )}
                </>
            )}

            {layout === "horizontal" && (
                <>
                    {renderTemplateCard(AvailableVolumeTemplates.HORIZONTAL_BASIC, "Free Gift with Purchase", {
                        default_offer: 1,
                        add_to_cart_cta_icon_type: "arrow",
                        add_to_cart_cta_text: "{icon} Add to Cart",
                        bundles_design: { ...liveConfig.bundles_design, default_cta: false },
                        hide_item_numbers: true,
                        volume_discounts: getOffers(3, {
                            1: {
                                allow_free_gifts: true,
                                free_gifts: [DUMMY_PRODUCTS.standardNoVariant],
                                discount_label_type: "flag",
                            },
                        }),
                    })}
                    {renderTemplateCard(
                        AvailableVolumeTemplates.HORIZONTAL_BASIC,
                        "Subscription Ready",
                        {
                            enable_subscription_integration: true,
                            products: [DUMMY_PRODUCTS.subscriptionWithVariant],
                            volume_discounts: getOffers(3, { 1: { discount_label_type: "flag" } }),
                            bundles_design: { ...liveConfig.bundles_design, default_cta: false },
                        },
                        "horizontal_basic_subscription",
                    )}
                    {renderTemplateCard(AvailableVolumeTemplates.HORIZONTAL_MODERN, "Savings Summary", {
                        default_offer: 1,
                        savings_summary: {
                            ...liveConfig.savings_summary,
                            status: true,
                            title: "{icon} You're saving {saved_amount} on this order {icon}",
                            icon_type: "star",
                        },
                        bundles_design: { ...liveConfig.bundles_design, default_cta: false },
                    })}
                    {renderTemplateCard(
                        AvailableVolumeTemplates.HORIZONTAL_MODERN,
                        "Countdown Timer & Unit Price",
                        {
                            show_unit_price: true,
                            show_countdown_timer: true,
                            add_to_cart_cta_icon_type: "arrow",
                            add_to_cart_cta_text: "{icon} Add to Cart",
                            volume_discounts: getOffers(3),
                            bundles_design: { ...liveConfig.bundles_design, default_cta: false },
                        },
                        "horizontal_modern_countdown_timer",
                    )}
                </>
            )}
        </div>
    );
};

export default VolumeTemplateLivePreview;
